'use client';

/**
 * EntityProfileContent — THE one entity-preview content component (Lam,
 * 2026-06-10: two containers, ONE content component).
 *
 * The entity graph's inspector and the shell-level EntityPreviewDrawer used to
 * carry two divergent copies of "the entity preview" — the inspector had typed
 * relationship labels + RECON, the drawer didn't. This file is the single
 * content core both containers now wrap:
 *
 *   • <GraphRail> in EntityGraph.tsx — desktop side rail + phone bottom sheet
 *     on /<chain>/entities (server-projected ChainEntity data, zero fetches).
 *   • <EntityPreviewDrawer> — the shell-level drawer every other hub surface
 *     opens (self-fetches /api/entity-profile/[slug] on open).
 *
 * Blocks (in order): header (avatar · type · optional chain chip · @handle with
 * the live-tracked dot) · token/asset chip strip · identity brief · RECON read ·
 * TYPED RELATIONSHIPS (perspective label + counterpart + narrative + confidence)
 * · ASSETS portfolio rows (junction rule: leaves live here, not on the canvas) ·
 * generic CONNECTIONS · external LINKS · container extras (children) · lazy
 * recent coverage · the canonical "View full profile →" footer.
 *
 * Renders a FRAGMENT of [header / chip strip / scrollable body / footer]: the
 * container owns the shell and must be a height-constrained flex-col — header,
 * chip and footer are flex-shrink-0 and ONLY the body scrolls (constant panel
 * size across entities; no layout shift).
 *
 * URL policy (Lam, 2026-06-10 — docs/superpowers/plans/2026-06-10-tags-migration-
 * panel-synthesis.md): `/tags/[slug]` stays canonical FOR NOW; the
 * `/entities/[slug]` rename ships as its own discrete post-push event.
 */

import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';
import { sanitizeUrl } from '@/utils/url-sanitizer';
import Link from 'next/link';
import { Avatar, Eyebrow, Mono } from './atoms';
import { ENTITY_TYPES, type EntityTypeKey } from './format';
import PriceChange from '@/components/hub/PriceChange';
import { XIcon } from '@/components/icons/SocialIcons';

/* ── props contract (structural — both containers' data shapes assign to it) ── */

/** A minimal entity reference (relationship counterpart / connection row). */
export interface ProfileEntityRef {
  slug: string;
  name: string;
  type: string;
  image?: string;
}

/** A typed relationship resolved from the PROFILED entity's perspective. */
export interface ProfileRelationship {
  /** Perspective label — e.g. 'founded' / 'founded by' / 'mascot of'. */
  label: string;
  other: ProfileEntityRef;
  /** Reader-facing narrative (the product — ASSOCIATED_WITH is ONLY explained by it). */
  description: string | null;
  confidence: string;
}

/** Trimmed RECON intel (the public display subset of twitterProfileAnalysis). */
export interface ProfileRecon {
  summary: string;
  sentiment: string;
  topics: string[];
  /** ISO timestamp — drives the "analyzed 6h ago" freshness stamp. */
  generatedAt: string;
}

/** A member-asset portfolio row (junction rule: leaves render here). */
export interface ProfileAssetRow {
  id: string;
  /** Raw asset id — SYMBOL for tokens, slug for collections. */
  assetId: string;
  assetType: 'token' | 'collection';
  name: string;
  image?: string;
  floor?: number | null;
  floorSymbol?: string;
}

/** An external link chip — [label, url]. */
export interface ProfileLinkChip {
  label: string;
  url: string;
}

export interface EntityProfileContentProps {
  entity: {
    id: string;
    slug: string;
    name: string;
    type: string;
    handle?: string;
    /** True when we ACTIVELY poll this handle (live dot next to @handle). */
    tracked?: boolean;
    image?: string;
  };
  /** ONE concise identity line; falls back to the coverage placeholder. */
  brief?: string;
  recon?: ProfileRecon | null;
  /** Token chip with live 24h change (graph inspector — server-joined price row). */
  token?: { symbol: string; change: number | null } | null;
  /** Identity-asset chip WITHOUT live pricing (drawer). `token` wins when both given. */
  assetChip?: {
    kind: 'token' | 'collection';
    label: string;
    marketHref: string;
    onNavigate?: () => void;
  } | null;
  relationships?: ProfileRelationship[];
  /** When provided, each relationship row gets a small ◆ affordance that promotes
      the edge to EDGE mode (edge-as-first-class). Graph inspector only; the shell
      drawer omits it (no graph edge-mode there). */
  onInspectRelationship?: (otherSlug: string) => void;
  portfolio?: ProfileAssetRow[];
  /** GENERIC connections — pre-deduped against `relationships` by the caller. */
  connections?: ProfileEntityRef[];
  links?: ProfileLinkChip[];
  /** Chain-membership chip in the header meta row (drawer); omit to hide (graph). */
  chainChip?: { color: string; label: string } | null;
  /** Re-focus/re-open a counterpart in place (relationship + connection rows). */
  onSelect: (slug: string) => void;
  /** False → that connection renders as a `/tags/[slug]` link instead. Default: all selectable. */
  isConnectionSelectable?: (slug: string) => boolean;
  /** Rendered inside the phone bottom sheet (tighter header, safe-area footer). */
  sheet?: boolean;
  /** Sheet only: swipe-down-to-dismiss handlers spread on the header strip. */
  dragRegionProps?: HTMLAttributes<HTMLDivElement>;
  /** Sheet only: the drag-region cursor/touch classes (leading space included). */
  dragRegionCls?: string;
  /** Right-aligned header slot — close button (rail) / share + close (drawer). */
  headerRight?: ReactNode;
  /** Container extras appended into the scroll body, before recent coverage. */
  children?: ReactNode;
}

/* ── component ───────────────────────────────────────────────────────────── */

export default function EntityProfileContent({
  entity,
  brief,
  recon = null,
  token = null,
  assetChip = null,
  relationships = [],
  onInspectRelationship,
  portfolio = [],
  connections = [],
  links = [],
  chainChip = null,
  onSelect,
  isConnectionSelectable,
  sheet = false,
  dragRegionProps,
  dragRegionCls = '',
  headerRight,
  children,
}: EntityProfileContentProps) {
  const typeLabel = ENTITY_TYPES[entity.type as EntityTypeKey]?.label ?? entity.type;
  const briefText = brief || 'Profile coverage is being built out for this entity.';
  const selectable = isConnectionSelectable ?? (() => true);

  return (
    <>
      {/* header — on the phone sheet this whole strip is the drag-to-dismiss region */}
      <div
        {...(dragRegionProps ?? {})}
        className={`flex flex-shrink-0 gap-3 border-b border-divider p-4${sheet ? ' pt-1.5' : ''}${dragRegionCls}`}
      >
        {entity.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={entity.image} alt="" className="h-11 w-11 flex-shrink-0 rounded object-cover" />
        ) : (
          <Avatar name={entity.name} type={entity.type} size={44} />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-base font-bold tracking-tight text-parchment">
            {entity.name}
          </h3>
          <div className="mt-0.5 flex items-center gap-2">
            <Mono size={10} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {typeLabel}
            </Mono>
            {chainChip && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: chainChip.color }}
                />
                <Mono size={10} color="var(--slate)">
                  {chainChip.label}
                </Mono>
              </span>
            )}
            {entity.handle && (
              <span className="inline-flex items-center gap-1">
                {entity.tracked && (
                  <span className="h-1.5 w-1.5 rounded-full bg-signal-green" aria-label="live" />
                )}
                <XIcon className="h-2.5 w-2.5 text-parchment-dim" />
                <Mono size={10} color="var(--slate)">
                  @{entity.handle}
                </Mono>
              </span>
            )}
          </div>
        </div>
        {headerRight}
      </div>

      {/* token chip (live change) — or the priceless identity-asset chip (drawer) */}
      {token ? (
        <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-divider px-4 py-2.5">
          <span className="font-mono text-[12px] font-semibold text-scout-gold">${token.symbol}</span>
          <PriceChange value={token.change} className="text-[12px]" />
        </div>
      ) : assetChip ? (
        <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-divider px-4 py-2.5">
          <span className="font-mono text-[12px] font-semibold text-scout-gold">{assetChip.label}</span>
          <Mono size={10} color="var(--slate)" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {assetChip.kind === 'token' ? 'Token' : 'Collection'}
          </Mono>
          <span className="flex-1" />
          <Link
            href={assetChip.marketHref}
            onClick={assetChip.onNavigate}
            className="font-mono text-[10px] uppercase tracking-[0.08em] text-scout-gold transition-opacity hover:opacity-80"
          >
            Market →
          </Link>
        </div>
      ) : null}

      {/* body — the ONLY part that scrolls, so the panel stays a constant size across
          entities regardless of how many connections each has (no layout shift).
          overscroll-contain: hitting either end never chains the scroll to the page
          behind an open sheet. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 scrollbar-hide">
        <p className="font-display text-[12.5px] leading-relaxed text-slate-light">{briefText}</p>

        {/* ── RECON intel — fresh AI social analysis (twitterProfileAnalysis),
            NOT the About. Absent (no tracked handle / no analysis doc) → the
            brief above stands alone; no empty shell. */}
        {recon && <ReconBlock recon={recon} />}

        {/* ── Typed relationships — label + counterpart + the NARRATIVE (the product;
            e.g. ASSOCIATED_WITH edges are only explained by their description). */}
        {relationships.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <Eyebrow color="var(--amber)">Relationships</Eyebrow>
              <Mono size={10} color="var(--slate)">
                {relationships.length}
              </Mono>
            </div>
            <div className="flex flex-col gap-1.5">
              {relationships.map((rel, i) => {
                const o = rel.other;
                return (
                  <div
                    key={`${o.slug}-${rel.label}-${i}`}
                    className="rounded border-[0.5px] border-divider bg-ink p-2"
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <Mono
                        size={9}
                        color="var(--amber)"
                        style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
                      >
                        {rel.label}
                      </Mono>
                      <span className="flex items-center gap-2">
                        <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em' }}>
                          {rel.confidence}
                        </Mono>
                        {onInspectRelationship && (
                          <button
                            type="button"
                            onClick={() => onInspectRelationship(o.slug)}
                            aria-label={`Inspect the ${rel.label} relationship`}
                            title="Inspect this relationship"
                            className="cursor-pointer font-mono text-[10px] leading-none text-slate-light transition-colors hover:text-scout-gold"
                          >
                            ◆
                          </button>
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSelect(o.slug)}
                      className="flex w-full items-center gap-2 rounded text-left transition-opacity hover:opacity-80"
                    >
                      {o.image ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={o.image} alt="" loading="lazy" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
                      ) : (
                        <Avatar name={o.name} type={o.type} size={24} />
                      )}
                      <span className="min-w-0 flex-1 truncate font-display text-[12px] font-semibold text-parchment">
                        {o.name}
                      </span>
                      <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        {ENTITY_TYPES[o.type as EntityTypeKey]?.label ?? o.type}
                      </Mono>
                    </button>
                    {rel.description && (
                      <p className="mt-1.5 font-display text-[11.5px] leading-relaxed text-slate-light">
                        {rel.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── ASSETS — the member-asset portfolio (junction rule: leaves render
            HERE as compact rows, not as canvas satellites). Name · type ·
            floor (collections, when tracked) · market deep-link per row; the
            identity token never appears (it's the header `$SYM` chip). */}
        {portfolio.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <Eyebrow color="var(--amber)">Assets</Eyebrow>
              <Mono size={10} color="var(--slate)">
                {portfolio.length}
              </Mono>
            </div>
            <div className="flex flex-col gap-1.5">
              {portfolio.map((a) => {
                const isToken = a.assetType === 'token';
                const href = `/market?tab=${isToken ? 'tokens' : 'nfts'}&asset=${a.assetId.toLowerCase()}`;
                return (
                  <Link
                    key={a.id}
                    href={href}
                    className="group flex items-center gap-2 rounded border-[0.5px] border-divider bg-ink p-1.5 transition-colors hover:border-scout-gold/40"
                  >
                    {a.image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={a.image} alt="" loading="lazy" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
                    ) : (
                      <span
                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-dashed bg-surface2 font-mono text-[9px]"
                        style={{ borderColor: 'var(--slate-light)', color: 'var(--slate-light)' }}
                        aria-hidden
                      >
                        {isToken ? '$' : '◆'}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate font-display text-[12px] font-semibold text-parchment">
                      {a.name}
                    </span>
                    {typeof a.floor === 'number' && a.floorSymbol && (
                      <Mono size={10} color="var(--parchment-dim)">
                        {formatFloor(a.floor)} {a.floorSymbol}
                      </Mono>
                    )}
                    <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {isToken ? 'Token' : 'NFT'}
                    </Mono>
                    {/* EXPLICIT market affordance — the destination is no longer a
                        silently-clickable name (the whole row is still the link). */}
                    <span className="ml-0.5 flex-shrink-0 font-mono text-[9px] uppercase tracking-[0.08em] text-slate-light transition-colors group-hover:text-scout-gold">
                      Market →
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Generic connections — associations with NO typed edge (typed wins;
            the caller pre-dedupes). Selectable rows re-focus in place; the rest
            link to their canonical page. */}
        {connections.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <Eyebrow color="var(--amber)">Connections</Eyebrow>
              <Mono size={10} color="var(--slate)">
                {connections.length}
              </Mono>
            </div>
            <div className="flex flex-col gap-1.5">
              {connections.map((r) => {
                const present = selectable(r.slug);
                const content = (
                  <>
                    {r.image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={r.image} alt="" loading="lazy" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
                    ) : (
                      <Avatar name={r.name} type={r.type} size={24} />
                    )}
                    <span className="min-w-0 flex-1 truncate font-display text-[12px] font-semibold text-parchment">
                      {r.name}
                    </span>
                    <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {ENTITY_TYPES[r.type as EntityTypeKey]?.label ?? r.type}
                    </Mono>
                  </>
                );
                const cls =
                  'flex items-center gap-2 rounded border-[0.5px] border-divider bg-ink p-1.5 text-left transition-colors hover:border-scout-gold/40';
                return present ? (
                  <button key={r.slug} type="button" onClick={() => onSelect(r.slug)} className={`w-full ${cls}`}>
                    {content}
                  </button>
                ) : (
                  <Link key={r.slug} href={`/tags/${r.slug}`} className={cls}>
                    {content}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* ── External links (drawer) — capped chips, X excluded (handle chip covers it). */}
        {links.length > 0 && (
          <div className="mt-4">
            <Eyebrow color="var(--slate)">Links</Eyebrow>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {links.map((l) => (
                <a
                  key={l.url}
                  href={sanitizeUrl(l.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded border-[0.5px] border-divider bg-ink px-2.5 py-1.5 font-mono text-[10.5px] text-parchment-dim transition-colors hover:border-scout-gold/40 hover:text-parchment"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* container extras (e.g. nothing today — slot kept for drift-free growth) */}
        {children}

        {/* ── Recent coverage — compact lazy list, fetched only while open. */}
        <InspectorCoverage tagId={entity.id} />
      </div>

      {/* footer — the deliberate jump to the canonical glossary surface */}
      <div
        className="flex-shrink-0 border-t border-divider p-3.5"
        style={sheet ? { paddingBottom: 'max(14px, env(safe-area-inset-bottom))' } : undefined}
      >
        <Link
          href={`/tags/${entity.slug}`}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-scout-gold transition-opacity hover:opacity-80"
        >
          View full profile →
        </Link>
      </div>
    </>
  );
}

/* ── RECON intel block (fresh social analysis, mono/gold language) ───────── */

/** Sentiment → display color (matches the dashboard's signal palette). */
const SENTIMENT_COLOR: Record<string, string> = {
  bullish: 'var(--green-text)',
  bearish: 'var(--red-text)',
  neutral: 'var(--slate-light)',
  informational: 'var(--slate-light)',
};

/** "analyzed 6h ago" freshness stamp from an ISO timestamp. */
function analyzedAgo(iso: string): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `analyzed ${Math.max(1, mins)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `analyzed ${hours}h ago`;
  return `analyzed ${Math.floor(hours / 24)}d ago`;
}

/**
 * Fresh AI social analysis for the profiled entity: sentiment chip + freshness
 * stamp, summary prose, key-topic chips. Only renders when an analysis exists
 * (the caller guards) — there is never an empty RECON shell.
 *
 * Client-only freshness math is hydration-safe here: both containers render
 * this block only after a client-side selection/open (no SSR render).
 */
function ReconBlock({ recon }: { recon: ProfileRecon }) {
  const stamp = analyzedAgo(recon.generatedAt);
  const sentimentColor = SENTIMENT_COLOR[recon.sentiment] ?? 'var(--slate-light)';
  return (
    <div className="mt-4 rounded border-[0.5px] border-scout-gold/25 bg-ink p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Eyebrow color="var(--amber)">Recon</Eyebrow>
          <span
            className="inline-flex items-center gap-1 rounded border-[0.5px] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.08em]"
            style={{ color: sentimentColor, borderColor: 'var(--divider)' }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: sentimentColor }}
              aria-hidden
            />
            {recon.sentiment}
          </span>
        </div>
        {stamp && (
          <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.04em' }}>
            {stamp}
          </Mono>
        )}
      </div>
      <p className="font-display text-[12px] leading-relaxed text-parchment-dim">
        {recon.summary}
      </p>
      {recon.topics.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {recon.topics.map((t) => (
            <span
              key={t}
              className="rounded border-[0.5px] border-divider bg-surface px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.06em] text-slate-light"
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── small format helpers ─────────────────────────────────────────────────── */

/** Compact floor-price formatter for portfolio rows ("0.042" / "1.25" / "1,200"). */
function formatFloor(v: number): string {
  if (v >= 100) return Math.round(v).toLocaleString('en-US');
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(3);
}

/* ── Recent coverage (originally ported from the retired slice-1 drawer) ──── */

/** Minimal breaking-news shape we read for the compact coverage list. */
interface CoverageItem {
  id: string;
  slug: string;
  title: string;
  featuredImage?: string;
  publishedAt?: string;
  createdAt?: string;
}

/** A usable thumbnail = a featuredImage that ISN'T an embedded tweet URL. */
function coverImage(item: CoverageItem): string | undefined {
  const img = item.featuredImage;
  if (!img) return undefined;
  if (/(?:twitter\.com|x\.com)\/[\w]+\/status\/\d+/.test(img)) return undefined;
  return img;
}

function relTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Recent coverage — a compact list (small thumbnail + title + time) fetched
 * lazily while the entity is inspected; omits the section entirely when empty.
 * Same `/api/breaking-news` read BreakingNewsByTag uses (feature parity).
 */
function InspectorCoverage({ tagId, limit = 4 }: { tagId: string; limit?: number }) {
  const [items, setItems] = useState<CoverageItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    setItems([]); // reset between entities (no stale coverage flash)
    (async () => {
      try {
        const res = await fetch(
          `/api/breaking-news?tagId=${tagId}&limit=20&status=published&sortBy=publishedAt`,
        );
        if (!res.ok) return;
        const data = await res.json();
        const news = (data?.data?.breakingNews ?? []) as CoverageItem[];
        if (!cancelled) setItems(news.slice(0, limit));
      } catch {
        /* degrade to no coverage */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tagId, limit]);

  if (items.length === 0) return null;

  return (
    <div className="mt-4">
      <Eyebrow color="var(--slate)">Recent coverage</Eyebrow>
      <div className="mt-1">
        {items.map((n) => {
          const img = coverImage(n);
          return (
            <Link
              key={n.id}
              href={`/breaking/${n.slug}`}
              className="group flex items-start gap-3 border-t border-divider py-2.5"
            >
              {img && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={img}
                  alt=""
                  loading="lazy"
                  className="h-10 w-12 flex-shrink-0 rounded object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 font-display text-[12px] font-semibold leading-snug text-parchment transition-colors group-hover:text-scout-gold">
                  {n.title}
                </div>
                <Mono size={10} color="var(--slate)">
                  {relTime(n.publishedAt || n.createdAt)}
                </Mono>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
